// src/components/hooks/useUploadWorkerAvatar.ts

import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

interface UseUploadWorkerAvatarProps {
  userId: string;
  onUploaded?: (url: string) => void;
  onRemoved?: () => void;
}

const POSSIBLE_EXTS = ['png', 'jpg', 'jpeg', 'webp'];

export const useUploadWorkerAvatar = ({
  userId,
  onUploaded,
  onRemoved,
}: UseUploadWorkerAvatarProps) => {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const uploadAvatar = useCallback(
    async (files: FileList | null) => {
      try {
        if (!files || files.length === 0) {
          toast.error('Debes seleccionar un archivo');
          return;
        }

        if (!userId) {
          toast.error('Usuario inválido');
          return;
        }

        setUploading(true);

        const file = files[0];
        const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
        const fileName = `${userId}.${ext}`;
        const filePath = `avatars/${fileName}`;

        await supabase.storage
          .from('bookoll')
          .remove(POSSIBLE_EXTS.map((e) => `avatars/${userId}.${e}`));

        const { error: uploadError } = await supabase.storage
          .from('bookoll')
          .upload(filePath, file, {
            upsert: true,
            contentType: file.type,
          });

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from('bookoll')
          .getPublicUrl(filePath);
        const publicUrl = `${data.publicUrl}?v=${Date.now()}`;

        const { error: updateError } = await supabase.auth.updateUser({
          data: { foto: publicUrl },
        });

        if (updateError) throw updateError;

        toast.success('Foto actualizada correctamente');
        onUploaded?.(publicUrl);
      } catch (err) {
        Sentry.captureException(err, { extra: { userId } });
        toast.error('Error al subir la foto');
      } finally {
        setUploading(false);
      }
    },
    [userId, onUploaded]
  );

  const removeAvatar = useCallback(async () => {
    try {
      if (!userId) {
        toast.error('Usuario inválido');
        return;
      }

      setRemoving(true);

      const { error: removeError } = await supabase.storage
        .from('bookoll')
        .remove(POSSIBLE_EXTS.map((e) => `avatars/${userId}.${e}`));

      if (removeError) throw removeError;

      const { error: updateError } = await supabase.auth.updateUser({
        data: { foto: null },
      });

      if (updateError) throw updateError;

      toast.success('Foto eliminada correctamente');
      onRemoved?.();
    } catch (err) {
      Sentry.captureException(err, { extra: { userId } });
      toast.error('Error al borrar la foto');
      throw err;
    } finally {
      setRemoving(false);
    }
  }, [userId, onRemoved]);

  return {
    uploading,
    removing,
    uploadAvatar,
    removeAvatar,
  };
};